import { Env } from '@env';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { format } from 'date-fns';
import { Image } from 'expo-image';
import {
  Stack,
  useFocusEffect,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import { t } from 'i18next';
import * as React from 'react';
import {
  Dimensions,
  Platform,
  Pressable,
  ScrollView,
  View,
} from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import type { ICarouselInstance } from 'react-native-reanimated-carousel';
import Carousel, { Pagination } from 'react-native-reanimated-carousel';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import type { Extra, Reservation } from '@/api';
import { useCustomer } from '@/api/customers/use-customer';
import { useSetupPaymentIntent } from '@/api/customers/use-setup-payment-intent';
import type { CreateReservationResponse } from '@/api/reservations/use-create-reservation';
import { useCreateReservation } from '@/api/reservations/use-create-reservation';
import { useCredits } from '@/api/reservations/use-credits';
import { useFinalizeReservation } from '@/api/reservations/use-finalize-reservation';
import { useReservation } from '@/api/reservations/use-reservation';
import { useResetReservationPaymentIntent } from '@/api/reservations/use-reset-reservation-payment-intent';
import { Canvas } from '@/components/canvas';
import {
  formatNumber,
  getCarContentData,
  getCarImages,
  isDesktop,
  isInsuranceFree,
  openAddressInMaps,
  rgbaToHex,
  snakeToTitleCase,
  useAuth,
} from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import { useTrackMetaEvent } from '@/core/hooks/use-track-meta-event';
import { useAttributionStore } from '@/stores/use-attribution-store';
import useBookCarStore from '@/stores/use-book-car';
import useCheckoutStore from '@/stores/use-checkout';
import { PINKLING } from '@/style/colors';
import { Button, Card, Input, showErrorMessage, Text } from '@/ui';
import { CreditsDiscount } from '@/ui/credits-discount';
import { Dialog } from '@/ui/dialog';
import { Icon } from '@/ui/icon';
import { Car as CarIcon, Docs, Person, Phone, Road, Wallet } from '@/ui/icons';
import { Calendar as CalendarIcon } from '@/ui/icons/calendar';
import { ChevronLeft } from '@/ui/icons/chevron-left';
import { ChevronRight } from '@/ui/icons/chevron-right';
import { Loader } from '@/ui/loader';

import BackButton from '../back-button';
import { NavigationButton, NavigationButtonTypes } from '../navigation-button';

const PAGE_HEIGHT = Dimensions.get('window').height;

const stripePromise = loadStripe(Env.STRIPE_PUBLISHABLE_KEY);

const StripeWrapper = () => {
  const { theme } = useStyles(stylesheet);
  const router = useRouter();
  const local = useLocalSearchParams<{
    reservation_id: string;
    spis: string; // stripe payment intent secret
    scs: string; // stripe customer secret
  }>();

  const reservation = useCheckoutStore((state) => state.reservation);
  const setReservation = useCheckoutStore((state) => state.setReservation);
  const stripeSecret = useCheckoutStore((state) => state.stripeSecret);
  const setStripeSecret = useCheckoutStore((state) => state.setStripeSecret);
  const customerSessionSecret = useCheckoutStore(
    (state) => state.customerSessionSecret
  );
  const setCustomerSessionSecret = useCheckoutStore(
    (state) => state.setCustomerSessionSecret
  );
  const setStripeCustomerId = useCheckoutStore(
    (state) => state.setStripeCustomerId
  );

  const startDatetime = useBookCarStore((state) => state.startDatetime);
  const endDatetime = useBookCarStore((state) => state.endDatetime);
  const deliveryModeEnabled = useBookCarStore(
    (state) => state.deliveryModeEnabled
  );
  const deliveryDeliverToAddress = useBookCarStore(
    (state) => state.deliveryDeliverToAddress
  );
  const deliveryReturnAddress = useBookCarStore(
    (state) => state.deliveryReturnAddress
  );
  const car = useBookCarStore((state) => state.car);
  const insurance = useBookCarStore((state) => state.insurance);
  const cancellationPolicy = useBookCarStore(
    (state) => state.cancellationPolicy
  );
  const discount = useBookCarStore((state) => state.discount);

  const [reservationId, setReservationId] = React.useState<number | null>(null);

  const { mutate: createReservation, isPending: isCreatingReservation } =
    useCreateReservation();

  const { data: resData, isPending: isFetchingReservation } = useReservation({
    //@ts-ignore
    variables: { id: reservationId },
    enabled: !!reservationId,
  });

  React.useEffect(() => {
    if (!reservationId && local.reservation_id) {
      setReservationId(Number(local.reservation_id));
      return;
    }

    if (reservationId) {
      router.setParams({ reservation_id: reservationId.toString() });
    }
  }, [reservationId, local.reservation_id, router]);

  React.useEffect(() => {
    if (!resData) return;
    setReservation(resData);
  }, [resData, setReservation]);

  React.useEffect(() => {
    if (!stripeSecret && local.spis) {
      setStripeSecret(local.spis);
      return;
    }

    if (stripeSecret) {
      router.setParams({ spis: stripeSecret });
    }
  }, [stripeSecret, local.spis, router]);

  React.useEffect(() => {
    if (!customerSessionSecret && local.scs) {
      setCustomerSessionSecret(local.scs);
      return;
    }

    if (customerSessionSecret) {
      router.setParams({ scs: customerSessionSecret });
    }
  }, [customerSessionSecret, local.scs, router]);

  const { pid, c } = useAttributionStore();

  useFocusEffect(
    React.useCallback(() => {
      if (
        !car ||
        !insurance ||
        !cancellationPolicy ||
        !startDatetime ||
        !endDatetime
      ) {
        return;
      }

      if (
        reservation &&
        reservation.insurance.id === insurance.id &&
        reservation.cancellationPolicy.id === cancellationPolicy.id &&
        reservation.car.id === car.id &&
        ((deliveryModeEnabled === false && !reservation.customPickupLocation) ||
          (deliveryModeEnabled &&
            reservation.customPickupLocation ===
              deliveryDeliverToAddress?.description &&
            reservation.customReturnLocation ===
              deliveryReturnAddress?.description)) &&
        ((!discount && reservation.discounts.length === 0) ||
          (discount?.code &&
            reservation.discounts.some((d) => d.code === discount?.code)))
      ) {
        setReservationId(reservation.id);
        return;
      }

      createReservation(
        {
          startDatetime: format(startDatetime, 'yyyy-MM-dd HH:mm:ss'),
          endDatetime: format(endDatetime, 'yyyy-MM-dd HH:mm:ss'),
          carId: car.id,
          insuranceId: insurance.id,
          cancellationPolicyId: cancellationPolicy.id,
          deliveryDeliverToAddress:
            (deliveryModeEnabled && deliveryDeliverToAddress?.description) ||
            undefined,
          deliveryReturnAddress:
            (deliveryModeEnabled && deliveryReturnAddress?.description) ||
            undefined,
          discountCode: discount?.code,
          pid: pid,
          c: c,
        },
        {
          onSuccess: (respData: CreateReservationResponse) => {
            setStripeSecret(respData.data.stripeSecret);
            setReservationId(respData.data.reservationId);
            setCustomerSessionSecret(respData.data.customerSessionSecret);
            setStripeCustomerId(respData.data.stripeCustomerId);
          },
          onError: (e) => {
            showErrorMessage(e.message);
          },
        }
      );
    }, [
      startDatetime,
      endDatetime,
      car,
      insurance,
      cancellationPolicy,
      deliveryDeliverToAddress,
      deliveryReturnAddress,
      discount,
    ])
  );

  if (!stripePromise || !stripeSecret || !customerSessionSecret || !resData) {
    return (
      <>
        <Stack.Screen
          options={{
            headerTitle: 'Checkout',
            headerLeft: () => <BackButton />,
            headerTitleAlign: 'center',
            headerShadowVisible: false,
            headerStyle: {
              backgroundColor: theme.colors.background_1,
            },
            headerTintColor: theme.colors.text.primary,
          }}
        />

        <View
          style={{
            width: '100%',
            height: '100%',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: theme.colors.background_1,
          }}
        >
          <Loader style={{ height: 250, width: 250 }} />
        </View>
      </>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        locale: 'en',
        clientSecret: stripeSecret,
        customerSessionClientSecret: customerSessionSecret,
        appearance: {
          variables: {
            colorBackground: rgbaToHex(theme.colors.background_2),
          },
          rules: {
            '.Tab': {
              border: 'none',
            },
          },
          theme: UnistylesRuntime.themeName === 'dark' ? 'night' : 'stripe',
        },
      }}
    >
      <CheckoutForm
        reservation={resData}
        setReservationId={setReservationId}
        stripeSecret={stripeSecret}
      />
    </Elements>
  );
};

function CheckoutForm({
  reservation,
  setReservationId,
}: {
  reservation: Reservation;
  setReservationId: (id: number) => void;
}) {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const stripe = useStripe();
  const elements = useElements();

  const stripeSecret = useCheckoutStore((state) => state.stripeSecret);
  const customerSessionSecret = useCheckoutStore(
    (state) => state.customerSessionSecret
  );

  const setReservation = useCheckoutStore((state) => state.setReservation);
  const startDatetime = useBookCarStore((state) => state.startDatetime);
  const endDatetime = useBookCarStore((state) => state.endDatetime);
  const car = useBookCarStore((state) => state.car);
  const insurance = useBookCarStore((state) => state.insurance);
  const discount = useBookCarStore((state) => state.discount);

  const [scrollEnabled, setScrollEnabled] = React.useState(true);
  const [signature, setSignature] = React.useState<string | undefined>();
  const [creditsUsed, setCreditsUsed] = React.useState<number | undefined>(0);
  const [isFormValid, setIsFormValid] = React.useState(false);
  const [paymentMethodId, setPaymentMethodId] = React.useState<
    string | undefined
  >();
  const status = useAuth.use.status();
  const scrollViewRef = React.useRef<ScrollView>(null);
  const signatureViewRef = React.useRef<View>(null);
  const paymentFormViewRef = React.useRef<View>(null);
  const [signatureError, setSignatureError] = React.useState<string | null>();
  const [isBooking, setIsBooking] = React.useState(false);
  const progress = useSharedValue<number>(0);
  const carouselRef = React.useRef<ICarouselInstance>(null);
  const [carouselActiveIndex, setCarouselActiveIndex] = React.useState(0);
  const [contentWidth, setContentWidth] = React.useState(0);

  const trackMetaEvent = useTrackMetaEvent();

  React.useEffect(() => {
    if (!reservation) return;

    mixpanelService.track('Page Viewed', {
      'Page Name': 'Checkout',
      'Selected Trip Days': reservation.numDays,
    });
  }, [reservation]);

  const {
    mutateAsync: finalizeReservation,
    isPending: isFinalizingReservation,
  } = useFinalizeReservation();

  const { mutateAsync: createSetupIntent, isPending: isSettingUpIntent } =
    useSetupPaymentIntent();

  const { mutateAsync: updateCredits, isPending: isUpdatingCredits } =
    useCredits();

  const {
    mutateAsync: resetPaymentIntent,
    isPending: isResettingPaymentIntent,
  } = useResetReservationPaymentIntent();

  const { data: customer } = useCustomer();

  React.useEffect(() => {
    if (status !== 'signIn') {
      router.back();
    }
  }, [status]);

  // Both booking paths converge here — an already signed-in user is pushed
  // straight to checkout, a signed-out one lands here via returnTo after
  // logging in — so this is the first point where the customer (and the PII
  // Meta needs to match the event) is guaranteed to be loaded. Keyed on the
  // reservation so refetches don't refire it, but a genuinely new reservation
  // counts as a new checkout.
  const initiateCheckoutFiredForRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (!reservation || !customer) return;
    if (initiateCheckoutFiredForRef.current === reservation.id) return;

    initiateCheckoutFiredForRef.current = reservation.id;

    trackMetaEvent('InitiateCheckout', {
      type: 'daily',
      customData: {
        ...getCarContentData(reservation.car, {
          category: 'daily_rental',
          quantity: reservation.numDays,
          itemPrice: reservation.car.dailyRate,
        }),
        value: reservation.totalCost,
        currency: 'USD',
      },
    });
  }, [reservation, customer, trackMetaEvent]);

  const creditsCoverTotal = React.useMemo(() => {
    if (!reservation || !creditsUsed) return false;

    return reservation.totalCost <= creditsUsed / 100;
  }, [creditsUsed, reservation]);

  const onConfirmBookingPress = async () => {
    setIsBooking(true);
    try {
      await handleBooking();
    } finally {
      setIsBooking(false);
    }
  };

  const handleBooking = async () => {
    if (!isFormValid) {
      paymentFormViewRef.current?.measureLayout(
        scrollViewRef.current?.getInnerViewNode(),
        (x, y) => {
          scrollViewRef.current?.scrollTo({
            x: 0,
            y,
            animated: true,
          });
        }
      );
      return;
    }

    if (!signature) {
      setSignatureError('Please provide your signature');

      signatureViewRef.current?.measureLayout(
        scrollViewRef.current?.getInnerViewNode(),
        (x, y) => {
          scrollViewRef.current?.scrollTo({
            x: 0,
            y,
            animated: true,
          });
        }
      );

      return;
    }

    if (!stripe || !elements || !reservation) {
      // Stripe.js has not yet loaded.
      return;
    }

    const { error: submitError } = await elements.submit();
    if (submitError) {
      console.log('submit error', submitError);
      return;
    }

    let paymentMethod = paymentMethodId;
    // new card and don't need to charge card, use setupintent to add card
    // for later transactions like deposit, tolls, etc.
    if (paymentMethodId === '-1' && creditsUsed && creditsCoverTotal) {
      let setupIntentToken = null;
      try {
        setupIntentToken = await createSetupIntent();
      } catch (err) {
        console.error('setup intent error', err);
        return;
      }

      const { setupIntent, error } = await stripe.confirmSetup({
        elements,
        clientSecret: setupIntentToken,
        redirect: 'if_required',
      });

      if (error) {
        console.log('confirm setup error', error);
        return;
      }

      paymentMethod = setupIntent.payment_method as string;
    }

    if (creditsUsed && creditsCoverTotal) {
      // call finalize and return
      // setBookingInProgress(false);
      // onSuccessfulPayment(useCredits);
      await finalizeTrip();
      return;
    }

    let prevAmount = 0;
    let newAmount = 0;
    if (creditsUsed && !creditsCoverTotal) {
      try {
        const res = await updateCredits({
          id: reservation.id!,
          credits: creditsUsed!,
        });

        prevAmount = res.oldAmount;
        newAmount = res.newAmount;
      } catch (e) {
        console.error('update credits error', e);
        // setBookingInProgress(false);
        // alert(e);
        return;
      }
    }

    let errorDuringBooking = null;
    if (paymentMethod !== '-1') {
      const { error } = await stripe.confirmCardPayment(stripeSecret, {
        payment_method: paymentMethod as string,
      });
      errorDuringBooking = error;
    } else if (paymentMethod === '-1') {
      const { error } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });
      errorDuringBooking = error;
    }

    if (errorDuringBooking) {
      mixpanelService.track('Stripe Payment Failed', {
        'Payment Error Code': errorDuringBooking.code,
        'Payment Error Message': errorDuringBooking.message,
      });

      if (creditsUsed && !creditsCoverTotal) {
        try {
          await resetPaymentIntent({ id: reservation.id! });
        } catch (e) {
          return;
        }
      }

      console.log('error', errorDuringBooking.message);
      return;
    } else {
      mixpanelService.track('Stripe Payment Succeeded');
    }

    await finalizeTrip();
  };

  const finalizeTrip = async () => {
    try {
      const respData = await finalizeReservation({
        id: reservation.id!,
        signature: signature!,
        credits: creditsUsed!,
      });

      const booking = respData.data;
      trackMetaEvent('Purchase', {
        type: 'daily',
        eventId: stripeSecret ? stripeSecret.split('_secret_')[0] : undefined,
        customData: {
          ...getCarContentData(reservation.car, {
            category: 'daily_rental',
            quantity: reservation.numDays,
            itemPrice: reservation.car.dailyRate,
          }),
          value: reservation.totalCost,
          currency: 'USD',
          order_id: booking.id,
        },
      });

      setReservation(null);
      setReservationId(null);
      router.navigate(`/checkout-complete/${respData.data.id}`);
    } catch (e: any) {
      showErrorMessage(e.message);
    }
  };

  const onPressPagination = (index: number) => {
    carouselRef.current?.scrollTo({
      /**
       * Calculate the difference between the current index and the target index
       * to ensure that the carousel scrolls to the nearest index
       */
      count: index - progress.value,
      animated: false,
    });

    setCarouselActiveIndex(index);
  };

  if (!reservation || !stripeSecret || !customerSessionSecret) {
    return (
      <>
        <Stack.Screen
          options={{
            headerShown: false,
          }}
        />

        <View
          style={{
            width: '100%',
            height: '100%',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: theme.colors.background_1,
          }}
        >
          <Loader style={{ height: 250, width: 250 }} />
        </View>
      </>
    );
  }

  return (
    <View style={styles.container}>
      {Platform.OS === 'web' && isDesktop() ? (
        <Stack.Screen
          options={{
            headerShown: false,
          }}
        />
      ) : (
        <Stack.Screen
          options={{
            headerTitle: 'Checkout',
            headerLeft: () => (
              <BackButton
                onPress={() => {
                  router.navigate(
                    `/car/${reservation.car.id}?start_datetime=${format(
                      startDatetime,
                      'yyyy-MM-dd HH:mm:ss'
                    )}&end_datetime=${format(
                      endDatetime,
                      'yyyy-MM-dd HH:mm:ss'
                    )}`
                  );
                }}
              />
            ),
            headerTitleAlign: 'center',
            headerShadowVisible: false,
            headerStyle: {
              backgroundColor: theme.colors.background_1,
            },
            headerTintColor: theme.colors.text.primary,
          }}
        />
      )}

      <ScrollView
        ref={scrollViewRef}
        showsVerticalScrollIndicator={false}
        style={styles.scrollViewContainer}
        contentContainerStyle={{
          alignItems: 'center',
          flexGrow: 1,
        }}
        scrollEnabled={scrollEnabled}
      >
        <View
          style={styles.scrollViewChild}
          onLayout={(event) => {
            const { width } = event.nativeEvent.layout;
            setContentWidth(width);
          }}
        >
          {['md', 'lg'].includes(UnistylesRuntime.breakpoint) ? (
            <View style={{ gap: 16 }}>
              <NavigationButton
                type={NavigationButtonTypes.back_with_text}
                onPress={() => {
                  router.navigate(
                    `/car/${reservation.car.id}?start_datetime=${format(
                      startDatetime,
                      'yyyy-MM-dd HH:mm:ss'
                    )}&end_datetime=${format(
                      endDatetime,
                      'yyyy-MM-dd HH:mm:ss'
                    )}`
                  );
                }}
              />

              <Text type="largeTitle">Checkout</Text>
            </View>
          ) : null}

          {contentWidth ? (
            <View
              style={{
                width: '100%',
                height: isDesktop() ? 300 : 200,
                position: 'relative',
                marginBottom: 32,
              }}
              id="carousel-component"
              dataSet={{ kind: 'basic-layouts', name: 'parallax' }}
            >
              {carouselActiveIndex > 0 ? (
                <Pressable
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: 0,
                    zIndex: 1,
                  }}
                  onPress={() => {
                    carouselRef.current?.scrollTo({
                      count: -1,
                      animated: true,
                    });
                  }}
                >
                  <Icon
                    type="dressed"
                    icon={<ChevronLeft />}
                    fillColor={theme.colors.icon.color}
                    width={12}
                    height={12}
                    style={{ width: 36, height: 36 }}
                  />
                </Pressable>
              ) : null}

              <Carousel
                ref={carouselRef}
                width={contentWidth}
                style={{
                  width: '100%',
                }}
                height={isDesktop() ? 300 : 200}
                data={getCarImages(reservation.car.images, 'small')}
                mode="parallax"
                modeConfig={{
                  parallaxScrollingScale: 1,
                  parallaxScrollingOffset: 0,
                }}
                windowSize={1}
                loop={false}
                onProgressChange={progress}
                onSnapToItem={(index) => {
                  setCarouselActiveIndex(index);
                }}
                renderItem={({ item }) => {
                  return (
                    <Image
                      source={{ uri: item.url }}
                      style={{
                        width: '95%',
                        aspectRatio: 16 / 9,
                        borderRadius: 16,
                      }}
                      contentFit="cover"
                    />
                  );
                }}
              />

              {carouselActiveIndex < 4 ? (
                <Pressable
                  style={{
                    position: 'absolute',
                    top: '50%',
                    right: 0,
                    zIndex: 1,
                  }}
                  onPress={() => {
                    carouselRef.current?.scrollTo({
                      count: 1,
                      animated: true,
                    });
                  }}
                >
                  <Icon
                    type="dressed"
                    icon={<ChevronRight />}
                    fillColor={theme.colors.icon.color}
                    width={12}
                    height={12}
                    style={{ width: 36, height: 36 }}
                  />
                </Pressable>
              ) : null}

              <Pagination.Basic
                progress={progress}
                data={getCarImages(reservation.car.images)}
                dotStyle={{
                  backgroundColor: theme.colors.paginationDots.empty,
                  borderRadius: 50,
                }}
                activeDotStyle={{
                  backgroundColor: theme.colors.paginationDots.filled,
                }}
                size={8}
                containerStyle={{ gap: 10, marginTop: 20 }}
                onPress={onPressPagination}
              />
            </View>
          ) : null}

          <Card>
            <View
              style={{
                borderBottomColor: theme.colors.card.default.borderColor,
                borderBottomWidth: 1,
              }}
            >
              <View style={{ padding: 16 }}>
                <Text type="title2">Pickup & Return Details</Text>
              </View>
            </View>

            <View>
              <View style={{ padding: 16, gap: 40 }}>
                <View style={{ gap: 8, flexDirection: 'row' }}>
                  <View style={{ flex: 1 }}>
                    <Text
                      tx={
                        reservation.customPickupLocation &&
                        reservation.customReturnLocation
                          ? 'carId.deliveryLocation'
                          : 'carId.pickupLocation'
                      }
                      type="bodyBold"
                    />

                    <Text
                      type="bodyRegular"
                      style={{ color: theme.colors.text.secondary }}
                    >
                      {format(startDatetime, 'MMM dd - hh:mm a')}
                    </Text>

                    <Button
                      type="link"
                      onPress={() => {
                        openAddressInMaps(
                          reservation.customPickupLocation ||
                            reservation.car.defaultLocation.address
                        );
                      }}
                      style={{
                        width: '100%',
                        height: 20,
                        paddingTop: 0,
                        paddingRight: 0,
                        paddingBottom: 0,
                        paddingLeft: 0,
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                      }}
                    >
                      <Text
                        type="bodyRegular"
                        style={{
                          textDecorationLine: 'underline',
                          color: theme.colors.text.secondary,
                        }}
                      >
                        {reservation.customPickupLocation ||
                          reservation.car.defaultLocation.address}
                      </Text>
                    </Button>
                  </View>
                </View>

                <View
                  style={{ gap: 8, flexDirection: 'row', paddingBottom: 16 }}
                >
                  <View style={{ flex: 1 }}>
                    <Text tx="carId.returnLocation" type="bodyBold" />

                    <Text
                      type="bodyRegular"
                      style={{ color: theme.colors.text.secondary }}
                    >
                      {format(new Date(endDatetime), 'MMM dd - hh:mm a')}
                    </Text>

                    <Button
                      type="link"
                      onPress={() => {
                        openAddressInMaps(
                          reservation.customReturnLocation ||
                            reservation.car.defaultLocation.address
                        );
                      }}
                      style={{
                        width: '100%',
                        height: 20,
                        paddingTop: 0,
                        paddingRight: 0,
                        paddingBottom: 0,
                        paddingLeft: 0,
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                      }}
                    >
                      <Text
                        type="bodyRegular"
                        style={{
                          textDecorationLine: 'underline',
                          color: theme.colors.text.secondary,
                        }}
                      >
                        {reservation.customReturnLocation ||
                          reservation.car.defaultLocation.address}
                      </Text>
                    </Button>
                  </View>
                </View>
              </View>
            </View>
          </Card>

          <Card>
            <View
              style={{
                backgroundColor: theme.colors.background_2,
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                borderRadius: 24,
              }}
            >
              <View
                style={{
                  padding: 16,
                  borderBottomColor: theme.colors.card.default.borderColor,
                  borderBottomWidth: 1,
                }}
              >
                <Text type="title2">Rental Price Breakdown</Text>
              </View>

              <View
                style={{
                  padding: 16,
                  gap: 8,
                  borderBottomColor: theme.colors.card.default.borderColor,
                  borderBottomWidth: 1,
                }}
              >
                <View style={{ gap: 4 }}>
                  <View>
                    <Text type="bodyBold">{`${
                      reservation.car.brandName
                    } ${snakeToTitleCase(
                      reservation.car.modelName
                    )} - ${snakeToTitleCase(reservation.car.modelType)}`}</Text>
                  </View>

                  <View
                    style={{
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}
                  >
                    <View
                      style={{
                        width: '33%',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Text
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        {`$${reservation.car.dailyRate} / day`}
                      </Text>
                    </View>

                    <View
                      style={{
                        width: '33%',
                        alignItems: 'center',
                      }}
                    >
                      <Text
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        {`x${reservation.numDays} days`}
                      </Text>
                    </View>

                    <View
                      style={{
                        width: '33%',
                        alignItems: 'flex-end',
                      }}
                    >
                      <Text
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        {`$${formatNumber(
                          reservation.car.dailyRate * reservation.numDays
                        )}`}
                      </Text>
                    </View>
                  </View>
                </View>

                {reservation.reservationRates
                  .filter((rate) => rate.discountAmount !== 0)
                  .map((rate) => {
                    return (
                      <View
                        style={{
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Text type="bodyRegular" style={{ color: PINKLING }}>
                          {rate.discountType}
                        </Text>

                        <Text type="bodyRegular" style={{ color: PINKLING }}>
                          {`-$${rate.discountAmount}`}
                        </Text>
                      </View>
                    );
                  })}

                <View style={{ gap: 4 }}>
                  <View>
                    <Text type="bodyBold">
                      {reservation.insurance.category}
                    </Text>
                  </View>

                  <View
                    style={{
                      width: '100%',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}
                  >
                    <View
                      style={{
                        width: '33%',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Text
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        {isInsuranceFree(reservation.insurance)
                          ? 'Free'
                          : `$${formatNumber(
                              reservation.insuranceCost / reservation.numDays
                            )} / day`}
                      </Text>
                    </View>

                    <View
                      style={{
                        width: '33%',
                        alignItems: 'center',
                      }}
                    >
                      <Text
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        {`x${reservation.numDays} days`}
                      </Text>
                    </View>

                    <View
                      style={{
                        width: '33%',
                        alignItems: 'flex-end',
                      }}
                    >
                      <Text
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        {`$${formatNumber(reservation.insuranceCost)}`}
                      </Text>
                    </View>
                  </View>
                </View>

                {reservation?.totalCancellationPolicyCost > 0 ? (
                  <View
                    style={{
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Text type="bodyBold">
                      {reservation.cancellationPolicy.title}
                    </Text>

                    <Text
                      type="bodyRegular"
                      style={{ color: theme.colors.text.secondary }}
                    >
                      {`$${formatNumber(
                        reservation.totalCancellationPolicyCost
                      )}`}
                    </Text>
                  </View>
                ) : null}

                {reservation.extras
                  .filter((extra: Extra) => extra.title !== 'Custom Delivery')
                  .map((extra: Extra) => {
                    return (
                      <View key={extra.id} style={{ gap: 4 }}>
                        {extra.recurring ? (
                          <View>
                            <Text type="bodyBold">{extra.title}</Text>

                            <View
                              style={{
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                              }}
                            >
                              <View
                                style={{
                                  width: '33%',
                                  alignItems: 'flex-start',
                                }}
                              >
                                <Text
                                  type="bodyRegular"
                                  style={{ color: theme.colors.text.secondary }}
                                >
                                  {`$${extra.amount} / day`}
                                </Text>
                              </View>

                              <View
                                style={{
                                  width: '33%',
                                  alignItems: 'center',
                                }}
                              >
                                <Text
                                  type="bodyRegular"
                                  style={{ color: theme.colors.text.secondary }}
                                >
                                  {`x${reservation.numDays} days`}
                                </Text>
                              </View>

                              <View
                                style={{
                                  width: '33%',
                                  alignItems: 'flex-end',
                                }}
                              >
                                <Text
                                  type="bodyRegular"
                                  style={{ color: theme.colors.text.secondary }}
                                >
                                  {`$${formatNumber(
                                    extra.amount * reservation.numDays
                                  )}`}
                                </Text>
                              </View>
                            </View>
                          </View>
                        ) : (
                          <View>
                            <Text type="bodyBold">{extra.title}</Text>

                            <Text>{`$${formatNumber(
                              extra.amount * reservation.numDays
                            )}`}</Text>
                          </View>
                        )}
                      </View>
                    );
                  })}

                {reservation.totalDeliveryCost > 0 ? (
                  <View
                    style={{
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Text type="bodyBold">Delivery Fee</Text>

                    <View>
                      <Text
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        {`$${formatNumber(reservation.totalDeliveryCost)}`}
                      </Text>
                    </View>
                  </View>
                ) : null}

                {creditsUsed ? (
                  <View
                    style={{
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Text type="bodyBold">Eon Credits</Text>

                    <View>
                      <Text
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        {`-$${formatNumber(creditsUsed / 100)}`}
                      </Text>
                    </View>
                  </View>
                ) : null}

                {discount ? (
                  <View
                    style={{
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Text type="bodyRegular" style={{ color: PINKLING }}>
                      {`Discount: ${discount.code}`}
                    </Text>

                    <Text type="bodyRegular" style={{ color: PINKLING }}>
                      {`-$${formatNumber(discount.amount)}`}
                    </Text>
                  </View>
                ) : null}
              </View>

              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  padding: 16,
                }}
              >
                <View>
                  <Text type="bodyBold">Total Rental Price</Text>
                </View>

                <View>
                  <Text type="bodyBold">{`$${formatNumber(
                    reservation.totalCost -
                      (creditsUsed ? creditsUsed / 100 : 0)
                  )}`}</Text>
                </View>
              </View>
            </View>
          </Card>

          <Card>
            <View
              style={{
                borderBottomColor: theme.colors.card.default.borderColor,
                borderBottomWidth: 1,
              }}
            >
              <View style={{ padding: 16 }}>
                <Text type="title2" tx="checkout.driversProfile" />
              </View>
            </View>

            <View style={{ padding: 16, gap: 16 }}>
              <View
                style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}
              >
                <Icon
                  type="dressed"
                  icon={<Person />}
                  fillColor={theme.colors.icon.color}
                  width={24}
                  height={24}
                />

                <Text type="bodyRegular">{reservation.customer.name}</Text>
              </View>

              <View style={{ gap: 16 }}>
                <Input
                  label="Name"
                  value={reservation.customer.name}
                  disabled
                />

                <Input
                  label="Email"
                  value={reservation.customer.email}
                  disabled
                />

                <Input
                  label="Phone"
                  value={reservation.customer.phone}
                  disabled
                />

                <Input
                  label="Date of Birth"
                  value={reservation.customer.dob}
                  disabled
                />
              </View>
            </View>
          </Card>

          <View
            style={{
              padding: 16,
              gap: 24,
              borderColor: theme.colors.card.default.borderColor,
              borderWidth: 1,
              borderRadius: 16,
            }}
          >
            <View style={{ gap: 16 }}>
              <Text tx="checkout.beforeYouBook" type="title2" />

              <View style={{ gap: 16 }}>
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <Icon
                    type="naked"
                    icon={<Wallet />}
                    fillColor={theme.colors.icon.color}
                    width={24}
                    height={24}
                    iconStyle={{
                      minWidth: 24,
                      minHeight: 24,
                    }}
                  />

                  <Text
                    type="bodyRegular"
                    style={{
                      color: theme.colors.text.secondary,
                      flexShrink: 1,
                    }}
                  >
                    Security deposit: A $500 authorization will be placed 24
                    hours before pickup and released once the vehicle is safely
                    returned. Certain vehicles — including Cybertrucks, Plaid
                    models, Rivians, Lucids, and rentals over 30 days — require
                    a $1,000 deposit.
                  </Text>
                </View>

                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <Icon
                    type="naked"
                    icon={<Road />}
                    fillColor={theme.colors.icon.color}
                    width={24}
                    height={24}
                    iconStyle={{
                      minWidth: 24,
                      minHeight: 24,
                    }}
                  />

                  <Text
                    type="bodyRegular"
                    style={{
                      color: theme.colors.text.secondary,
                      flexShrink: 1,
                    }}
                  >
                    Free 300 miles daily. Excess miles cost $0.85 per mile.
                  </Text>
                </View>

                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <Icon
                    type="naked"
                    icon={<CalendarIcon />}
                    fillColor={theme.colors.icon.color}
                    width={24}
                    height={24}
                    iconStyle={{
                      minWidth: 24,
                      minHeight: 24,
                    }}
                  />

                  <View style={{ gap: 16, flexShrink: 1 }}>
                    <Text
                      type="bodyRegular"
                      style={{
                        color: theme.colors.text.secondary,
                      }}
                    >
                      Cancellation policy
                    </Text>

                    <Text
                      type="bodyRegular"
                      style={{
                        color: theme.colors.text.secondary,
                      }}
                    >
                      Refundable: Free cancellation at any time (up to 1 hour
                      before trip start)
                    </Text>
                    <Text
                      type="bodyRegular"
                      style={{
                        color: theme.colors.text.secondary,
                      }}
                    >
                      Non-refundable: Free cancellation within 1 hour of
                      booking. No refund after that.
                    </Text>
                  </View>
                </View>
              </View>
            </View>

            <View style={{ gap: 16 }}>
              <Text tx="checkout.whatsNext" type="title2" />

              <View style={{ gap: 16 }}>
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <Icon
                    type="naked"
                    icon={<Docs />}
                    fillColor={theme.colors.icon.color}
                    width={24}
                    height={24}
                    iconStyle={{
                      minWidth: 24,
                      minHeight: 24,
                    }}
                  />

                  <Text
                    values={{ amount: 500 }}
                    type="bodyRegular"
                    style={{
                      color: theme.colors.text.secondary,
                      flexShrink: 1,
                    }}
                  >
                    After booking, first time users will need to upload their
                    license for verification.
                  </Text>
                </View>

                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <Icon
                    type="naked"
                    icon={<CarIcon />}
                    fillColor={theme.colors.icon.color}
                    width={24}
                    height={24}
                    iconStyle={{
                      minWidth: 24,
                      minHeight: 24,
                    }}
                  />

                  <Text
                    tx="checkout.receiveCarDetails"
                    values={{ miles: 300, amountPerMile: '0.85' }}
                    type="bodyRegular"
                    style={{
                      color: theme.colors.text.secondary,
                      flexShrink: 1,
                    }}
                  />
                </View>

                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <Icon
                    type="naked"
                    icon={<Phone />}
                    fillColor={theme.colors.icon.color}
                    width={24}
                    height={24}
                    iconStyle={{
                      minWidth: 24,
                      minHeight: 24,
                    }}
                  />

                  <Text
                    values={{ hours: 48, percent: 50 }}
                    type="bodyRegular"
                    style={{
                      color: theme.colors.text.secondary,
                      flexShrink: 1,
                    }}
                  >
                    Download the Eon app to control your car once your trip
                    starts.
                  </Text>
                </View>
              </View>
            </View>
          </View>

          <Card>
            <View ref={paymentFormViewRef} style={{ padding: 16 }}>
              <PaymentElement
                onChange={(e) => {
                  setIsFormValid(e.complete);
                  setPaymentMethodId(e.value?.payment_method?.id || '-1');
                }}
                options={{
                  business: { name: 'Eon' },
                  layout: {
                    type: 'accordion',
                    radios: true,
                    spacedAccordionItems: false,
                  },
                }}
              />
            </View>
          </Card>

          {reservation && customer ? (
            <CreditsDiscount
              keyboardType="numeric"
              checkboxEnabled
              checkboxTitle={t('checkout.applyEonCredits')}
              checkboxSubtitle={t('checkout.availableAmount', {
                amount: formatNumber((customer?.credits - creditsUsed) / 100),
              })}
              title={
                customer?.credits
                  ? t('checkout.availableEonCredits', {
                      amount: formatNumber(
                        (customer?.credits - creditsUsed) / 100
                      ),
                    })
                  : t('checkout.noEonCredits')
              }
              inputLabel={t('checkout.availableEonCreditsPlaceholder', {
                amount: formatNumber(
                  customer?.credits / 100 > reservation.totalCost
                    ? reservation.totalCost
                    : customer?.credits / 100
                ),
              })}
              buttonTx={'checkout.applyCredit'}
              credits={customer.credits / 100}
              onPress={(credits: number) => {
                setCreditsUsed(credits * 100);
              }}
              onCheck={(checked: boolean) => {
                if (!checked) {
                  setCreditsUsed(0);
                }
              }}
              available={customer.credits / 100}
              min={1}
              max={reservation?.totalCost}
              // disabled={discountCode.length === 0}
            />
          ) : null}

          <Card>
            <View
              style={{
                borderBottomColor: theme.colors.card.default.borderColor,
                borderBottomWidth: 1,
              }}
            >
              <View style={{ padding: 16 }}>
                <Text tx="checkout.digitalSignature" type="title2" />

                <Text tx="checkout.digitalSignatureBody" type="caption1" />
              </View>
            </View>

            <View ref={signatureViewRef} style={{ padding: 16, gap: 16 }}>
              <Canvas
                onDone={(sig: string) => {
                  setSignature(sig);
                  setSignatureError(null);
                }}
                onClear={() => {
                  setSignature(undefined);
                }}
              />

              {signatureError ? (
                <Text type="caption1" style={{ color: theme.colors.error }}>
                  {signatureError}
                </Text>
              ) : null}

              <Dialog
                header={<Text tx="checkout.termsAndConditions" type="title2" />}
                bodyJsx={
                  <Text type="bodyRegular">
                    Please review our policies. By making a payment, you
                    automatically accept our terms and conditions and our{' '}
                    <Pressable
                      onPress={() => {
                        window.open(
                          'https://www.eonrides.com/legal/terms-privacy-policy',
                          '_blank'
                        );
                      }}
                      style={{ textDecorationLine: 'underline' }}
                    >
                      <Text type="bodyRegular">Privacy Policy.</Text>
                    </Pressable>
                  </Text>
                }
                button={
                  <Button
                    text="Terms and Conditions"
                    type="secondaryExtraSmall"
                    onPress={() => {
                      window.open(`${Env.BASE_URL}/rental-terms`, '_blank');
                    }}
                    rightIcon={
                      <Icon
                        type="naked"
                        icon={<ChevronRight />}
                        fillColor={theme.colors.button.secondary.default.text}
                        width={10}
                        height={10}
                      />
                    }
                    style={styles.reviewPoliciesButton}
                  />
                }
              />
            </View>
          </Card>

          <View style={{ paddingBottom: 32 }}>
            {isDesktop() ? (
              <Button
                tx="checkout.confirmBooking"
                type="primary"
                onPress={onConfirmBookingPress}
                disabled={status !== 'signIn'}
                loading={isBooking}
                style={{
                  width: '100%',
                }}
              />
            ) : null}
          </View>
        </View>
      </ScrollView>

      <View style={styles.overviewContainer}>
        <View
          style={{
            flexGrow: 1,
            paddingLeft: 24,
            paddingRight: 24,
            paddingTop: 16,
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <View>
            <View
              style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
            >
              <Text type="bodyBold">{`Total $${formatNumber(
                reservation.totalCost - (creditsUsed ? creditsUsed / 100 : 0)
              )}`}</Text>
            </View>

            <View>
              <Text tx="checkout.includingTaxes" type="caption2" />
            </View>
          </View>

          <View>
            <Button
              tx="checkout.confirmBooking"
              type="primary"
              onPress={onConfirmBookingPress}
              disabled={status !== 'signIn'}
              loading={isBooking}
              style={{
                width: 180,
              }}
            />
          </View>
        </View>
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background_1,
    alignItems: 'center',
  },
  scrollViewContainer: {
    flex: {
      xs: 'none',
      md: 1,
    },
    maxHeight: {
      xs: PAGE_HEIGHT * 0.8,
      md: 'auto',
    },
    width: '100%',
  },
  scrollViewChild: {
    padding: 16,
    gap: 24,
    width: {
      xs: '100%',
      md: 600,
    },
    flexGrow: 1,
  },
  overviewContainer: {
    flexGrow: 1,
    display: {
      md: 'none',
    },
    width: '100%',
    backgroundColor: theme.colors.background_2,
  },
  reviewPoliciesButton: {
    width: {
      xs: '60%',
      md: 170,
    },
  },
}));

export default StripeWrapper;
